import Lottie from "lottie-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
  useNavigate,
} from "react-router-dom";
import "./App.css";
import waitingAnimation from "./assets/lottie/waiting_lottie.json";
import AccessDeniedPage from "./components/AccessDeniedPage";
import AdditionalNotes from "./components/AdditionalNotes";
import CategorySelection from "./components/CategorySelection";
import DocumentOrderSelector from "./components/DocumentOrderSelector";
import FileUpload from "./components/FileUpload";
import LoginPage from "./components/LoginPage";
import ResultSummary from "./components/ResultSummary";
import {
  DocumentTemplatesProvider,
  useDocumentTemplates,
} from "./context/DocumentTemplatesContext";
import { RequestProvider, useRequest } from "./context/RequestContext";
import { UploadProvider, useUploadContext } from "./context/UploadContext";
import { useAuth0Firebase } from "./hooks/useAuth0Firebase";

function AppContent({ onReturnToCategories, selectedCategoryId }) {
  const navigate = useNavigate();
  const {
    templates,
    loading: templatesLoading,
    error: templatesError,
    refetch,
  } = useDocumentTemplates();
  const { createNewRequest } = useRequest();
  // Load last selected template from localStorage if available
  const [documentType, setDocumentType] = useState(
    () => localStorage.getItem("selectedTemplateId") || ""
  );
  const { user, logout } = useAuth0Firebase();

  // Holds all field values extracted or entered in the current session
  const [documentData, setDocumentData] = useState({
    documentType: "",
    damagePayments: {},
    thirdPartyPayments: {},
    depreciationPayments: {},
  });

  // Filter templates by selected category (match parent field exactly)
  const filteredTemplates = useMemo(() => {
    if (!selectedCategoryId) return templates;
    return templates.filter(
      (template) => template.parent === selectedCategoryId
    );
  }, [templates, selectedCategoryId]);

  // If only one template is available, dropdown should be disabled/hidden
  const isSingleTemplate = filteredTemplates.length <= 1;

  // Debug logging for templates
  useEffect(() => {
    console.log("Templates from context:", templates);
    console.log("Filtered templates:", filteredTemplates);
    console.log("Templates loading:", templatesLoading);
    console.log("Templates error:", templatesError);
  }, [templates, filteredTemplates, templatesLoading, templatesError]);

  // Handle manual refetch of templates
  const handleRefetchTemplates = () => {
    console.log("Manually refetching templates...");
    if (typeof refetch === "function") {
      refetch();
    }
  };

  // Initialize document type with first template of the selected category when templates are loaded
  useEffect(() => {
    if (templatesLoading || filteredTemplates.length === 0) return;

    // Check if current documentType is part of the current filtered list
    const isCurrentInList = filteredTemplates.some(
      (template) => template.id === documentType
    );

    if (!isCurrentInList) {
      const initialTemplate = filteredTemplates[0].id;
      if (initialTemplate === "pre_created_document" && documentType) {
        setDocumentType(initialTemplate);
        return;
      }
      console.log(
        "Setting default template because none selected / current not in list:",
        initialTemplate
      );
      setDocumentType(initialTemplate);
      setDocumentData((prev) => ({ ...prev, documentType: initialTemplate }));
    }
  }, [filteredTemplates, templatesLoading, documentType]);

  // Reset selected template when category changes
  useEffect(() => {
    if (selectedCategoryId) {
      // Clear current selection to force re-initialisation based on new category
      // and skip the immediate reset that would otherwise be triggered.
      skipNextReset.current = true;
      setDocumentType("");
    }
  }, [selectedCategoryId]);

  const [additionalNotes, setAdditionalNotes] = useState("");
  const {
    allFileUploadsComplete,
    setRequiredUploadsForTemplate,
    resetUploadContextState,
  } = useUploadContext();
  const [documentOrder, setDocumentOrder] = useState([]);
  const [consolidationApplicationNumber, setConsolidationApplicationNumber] =
    useState("");
  const [fileUploadResetKey, setFileUploadResetKey] = useState(0);

  // Ref to track initial mount for documentType effect
  const isInitialDocumentTypeSet = useRef(true);
  // Ref to skip reset when documentType is set programmatically (e.g., pre-created flow)
  const skipNextReset = useRef(false);

  // Find selected template
  const selectedTemplate = useMemo(() => {
    if (!templatesLoading && documentType) {
      let template =
        filteredTemplates.find((template) => template.id === documentType) ||
        null;
      if (!template) {
        // precreated document case
        template = templates.find((template) => template.id === documentType);
      }
      console.log("Selected template:", template);
      return template;
    }
    return null;
  }, [filteredTemplates, documentType, templatesLoading]);

  useEffect(() => {
    if (selectedTemplate) {
      console.log("Selected template changed:", selectedTemplate);
      console.log("Template titles:", selectedTemplate.titles);
      console.log("Input documents:", selectedTemplate.inputDocuments);
      console.log("Optional documents:", selectedTemplate.optionalDocuments);
      // Set required uploads for the current template
      setRequiredUploadsForTemplate(
        selectedTemplate.inputDocuments,
        selectedTemplate.optionalDocuments
      );
    }
  }, [selectedTemplate, setRequiredUploadsForTemplate]);

  const calculatePayments = (documentData) => {
    const newDocumentData = { ...documentData };

    // Calculate damagePayments if damage_receipt data exists
    if (
      documentData.damage_receipt &&
      Array.isArray(documentData.damage_receipt)
    ) {
      console.log(
        "Calculating damagePayments from damage_receipt data:",
        documentData.damage_receipt
      );

      // Reset damagePayments to rebuild from current data
      newDocumentData.damagePayments = {};

      // Process all damage receipt data
      documentData.damage_receipt.forEach((d) => {
        if (d.status === "DONE" && d.transactionDate && d.amount) {
          console.log("Processing DONE damage receipt:", d);

          // Initialize date array if it doesn't exist
          if (!newDocumentData.damagePayments[d.transactionDate]) {
            newDocumentData.damagePayments[d.transactionDate] = [];
          }

          // Add the amount (this rebuilds the entire structure)
          newDocumentData.damagePayments[d.transactionDate].push(d.amount);
        }
      });

      console.log("Calculated damagePayments:", newDocumentData.damagePayments);
    }

    if (
      documentData.third_party_receipt &&
      Array.isArray(documentData.third_party_receipt)
    ) {
      console.log(
        "Calculating thirdPartyPayments from third_party_receipt data:",
        documentData.third_party_receipt
      );

      newDocumentData.thirdPartyPayments = {};

      documentData.third_party_receipt.forEach((d) => {
        if (d.status === "DONE" && d.transactionDate && d.amount) {
          console.log("Processing DONE third party receipt:", d);

          // Initialize date array if it doesn't exist
          if (!newDocumentData.thirdPartyPayments[d.thirdPartyPlate]) {
            newDocumentData.thirdPartyPayments[d.thirdPartyPlate] = {};
          }
          if (
            !newDocumentData.thirdPartyPayments[d.thirdPartyPlate][
              d.transactionDate
            ]
          ) {
            newDocumentData.thirdPartyPayments[d.thirdPartyPlate][
              d.transactionDate
            ] = [];
          }

          // Add the amount (this rebuilds the entire structure)
          newDocumentData.thirdPartyPayments[d.thirdPartyPlate][
            d.transactionDate
          ].push(d.amount);
        }
      });
    }

    if (
      documentData.recourse_receipt &&
      Array.isArray(documentData.recourse_receipt)
    ) {
      console.log(
        "Calculating recoursePayments from recourse_receipt data:",
        documentData.recourse_receipt
      );

      newDocumentData.recoursePayments = {};

      documentData.recourse_receipt.forEach((d) => {
        if (d.status === "DONE" && d.transactionDate && d.amount) {
          console.log("Processing DONE recourse receipt:", d);

          // Initialize date array if it doesn't exist
          if (!newDocumentData.recoursePayments[d.recoursePaymentCompany]) {
            newDocumentData.recoursePayments[d.recoursePaymentCompany] = {};
          }
          if (
            !newDocumentData.recoursePayments[d.recoursePaymentCompany][
              d.transactionDate
            ]
          ) {
            newDocumentData.recoursePayments[d.recoursePaymentCompany][
              d.transactionDate
            ] = [];
          }

          // Add the amount (this rebuilds the entire structure)
          newDocumentData.recoursePayments[d.recoursePaymentCompany][
            d.transactionDate
          ].push(d.amount);
        }
      });
    }

    if (
      documentData.depreciation_receipt &&
      Array.isArray(documentData.depreciation_receipt)
    ) {
      console.log(
        "Calculating depreciationPayments from depreciation_receipt data:",
        documentData.depreciation_receipt
      );

      newDocumentData.depreciationPayments = {};

      documentData.depreciation_receipt.forEach((d) => {
        if (d.status === "DONE" && d.transactionDate && d.amount) {
          console.log("Processing DONE depreciation receipt:", d);

          // Initialize date array if it doesn't exist
          if (!newDocumentData.depreciationPayments[d.transactionDate]) {
            newDocumentData.depreciationPayments[d.transactionDate] = [];
          }

          // Add the amount (this rebuilds the entire structure)
          newDocumentData.depreciationPayments[d.transactionDate].push(
            d.amount
          );
        }
      });

      console.log(
        "Calculated depreciationPayments:",
        newDocumentData.depreciationPayments
      );
    }

    return newDocumentData;
  };

  const handleDocumentDataUpdate = (type, data) => {
    console.log(`Received document data update for ${type}:`, data);
    const newDocumentData = {
      ...documentData,
      [type]: data,
      documentType: documentType,
    };

    if (data) {
      if (type === "petition") {
        data = data[0];
        newDocumentData.applicantName = data.applicantName;
        newDocumentData.applicantAttorney = data.applicantAttorney;
        newDocumentData.otherPartyLicensePlate = data.otherPartyLicensePlate;
        newDocumentData.applicantLicensePlate = data.applicantLicensePlate;
        newDocumentData.arbitrationNumber = data.arbitrationNumber;
      }
      if (type === "petition1") {
        data = data[0];
        newDocumentData.applicantName = data.applicantName;
        newDocumentData.applicantAttorney = data.applicantAttorney;
        newDocumentData.otherPartyLicensePlate = data.otherPartyLicensePlate;
        newDocumentData.applicantLicensePlate = data.applicantLicensePlate;
        newDocumentData.accidentDate = data.accidentDate;
        newDocumentData.arbitrationNumber = data.arbitrationNumber;
      }
      if (type === "petition2") {
        data = data[0];
        newDocumentData.applicantName = data.applicantName;
        newDocumentData.applicantAttorney = data.applicantAttorney;
        newDocumentData.otherPartyLicensePlate = data.otherPartyLicensePlate;
        newDocumentData.accidentDate = data.accidentDate;
        newDocumentData.arbitrationNumber = data.arbitrationNumber;
      }
      if (type === "petition3") {
        data = data[0];
        newDocumentData.applicantName = data.applicantName;
        newDocumentData.applicantAttorney = data.applicantAttorney;
        newDocumentData.otherPartyLicensePlate = data.otherPartyLicensePlate;
        newDocumentData.arbitrationNumber = data.arbitrationNumber;
      }
      if (type === "petition4") {
        data = data[0];
        newDocumentData.applicantName = data.applicantName;
        newDocumentData.applicantAttorney = data.applicantAttorney;
        newDocumentData.accidentDate = data.accidentDate;
        newDocumentData.arbitrationNumber = data.arbitrationNumber;
        newDocumentData.applicationDate = data.applicationDate;
      }
      if (type === "policy") {
        data = data[0];
        newDocumentData.policyNumber = data.policyNumber;
        newDocumentData.applicantLicensePlate = data.applicantLicensePlate;
      }
      if (type === "policy1") {
        data = data[0];
        newDocumentData.policyNumber = data.policyNumber;
        newDocumentData.applicantLicensePlate = data.applicantLicensePlate;
        newDocumentData.policyIssuanceTime = data.policyIssuanceTime;
        newDocumentData.policyStartDate = data.policyStartDate;
      }
      if (type === "expert_report") {
        data = data[0];
        newDocumentData.accidentDate = data.accidentDate;
        newDocumentData.totalDepreciation = data.totalDepreciation;
      }
      if (type === "expert_report1") {
        data = data[0];
        newDocumentData.accidentDate = data.accidentDate;
      }
      if (type === "expert_report2") {
        data = data[0];
        newDocumentData.totalDepreciation = data.totalDepreciation;
      }
      if (type === "expert_report3") {
        data = data[0];
        newDocumentData.totalDamage = data.totalDamage;
      }
      if (type === "authority_report") {
        data = data[0];
        newDocumentData.arbitrationNumber = data.arbitrationNumber;
        newDocumentData.applicantName = data.applicantName;
        newDocumentData.applicantAttorney = data.applicantAttorney;
      }
      if (type === "depreciation_receipt") {
        newDocumentData.depreciationPayments = {};

        data.forEach((d) => {
          if (d.status === "DONE" && d.transactionDate && d.amount) {
            console.log("Processing DONE depreciation receipt:", d);

            if (!newDocumentData.depreciationPayments[d.transactionDate]) {
              newDocumentData.depreciationPayments[d.transactionDate] = [];
            }

            // Add the amount (this rebuilds the entire structure)
            newDocumentData.depreciationPayments[d.transactionDate].push(
              d.amount
            );
          }
        });
      }
      if (type === "third_party_receipt") {
        newDocumentData.thirdPartyPayments = {};

        // Process all current third party receipt data
        data.forEach((d) => {
          if (d.status === "DONE" && d.transactionDate && d.amount) {
            console.log("Processing DONE third party receipt:", d);

            // Initialize date array if it doesn't exist
            if (!newDocumentData.thirdPartyPayments[d.thirdPartyPlate]) {
              newDocumentData.thirdPartyPayments[d.thirdPartyPlate] = {};
            }
            if (
              !newDocumentData.thirdPartyPayments[d.thirdPartyPlate][
                d.transactionDate
              ]
            ) {
              newDocumentData.thirdPartyPayments[d.thirdPartyPlate][
                d.transactionDate
              ] = [];
            }

            // Add the amount (this rebuilds the entire structure)
            newDocumentData.thirdPartyPayments[d.thirdPartyPlate][
              d.transactionDate
            ].push(d.amount);
          }
        });
      }
      if (type === "recourse_receipt") {
        newDocumentData.recoursePayments = {};

        // Process all current third party receipt data
        data.forEach((d) => {
          if (d.status === "DONE" && d.transactionDate && d.amount) {
            console.log("Processing DONE recourse receipt:", d);

            // Initialize date array if it doesn't exist
            if (!newDocumentData.recoursePayments[d.recoursePaymentCompany]) {
              newDocumentData.recoursePayments[d.recoursePaymentCompany] = {};
            }
            if (
              !newDocumentData.recoursePayments[d.recoursePaymentCompany][
                d.transactionDate
              ]
            ) {
              newDocumentData.recoursePayments[d.recoursePaymentCompany][
                d.transactionDate
              ] = [];
            }

            // Add the amount (this rebuilds the entire structure)
            newDocumentData.recoursePayments[d.recoursePaymentCompany][
              d.transactionDate
            ].push(d.amount);
          }
        });
      }
      if (type === "application_receipt") {
        data = data[0];
        newDocumentData.arbitrationApplicationDate = data.transactionDate;
      }
      if (type === "damage_receipt") {
        console.log("Processing damage_receipt data:", data);
        console.log(
          "Current damagePayments before update:",
          newDocumentData.damagePayments
        );

        // Reset damagePayments to rebuild from current data
        newDocumentData.damagePayments = {};

        // Process all current damage receipt data
        data.forEach((d) => {
          if (d.status === "DONE" && d.transactionDate && d.amount) {
            console.log("Processing DONE damage receipt:", d);

            // Initialize date array if it doesn't exist
            if (!newDocumentData.damagePayments[d.transactionDate]) {
              newDocumentData.damagePayments[d.transactionDate] = [];
            }

            // Add the amount (this rebuilds the entire structure)
            newDocumentData.damagePayments[d.transactionDate].push(d.amount);
          }
        });

        console.log("Updated damagePayments:", newDocumentData.damagePayments);
      }
      if (type === "arbitration_decision") {
        data = data[0];
        newDocumentData.concludedArbitrationCaseNumber =
          data.concludedArbitrationCaseNumber;
        newDocumentData.concludedArbitrationDecisionDate =
          data.concludedArbitrationDecisionDate;
        newDocumentData.concludedArbitrationDecisionNumber =
          data.concludedArbitrationDecisionNumber;
        newDocumentData.paymentAmountInConcludedCase =
          data.paymentAmountInConcludedCase;
      }
      if (type === "arbitration_decision1") {
        data = data[0];
        newDocumentData.concludedArbitrationDecisionDate =
          data.concludedArbitrationDecisionDate;
        newDocumentData.concludedArbitrationDecisionNumber =
          data.concludedArbitrationDecisionNumber;
        newDocumentData.paymentAmountInConcludedCase =
          data.paymentAmountInConcludedCase;
      }

      if (data.arbitrationNumber)
        newDocumentData.arbitrationNumber = data.arbitrationNumber;
      if (data.consolidationApplicationNumber)
        newDocumentData.consolidationApplicationNumber =
          data.consolidationApplicationNumber;
      setDocumentData((prev) => ({
        ...prev,
        ...newDocumentData,
      }));
    } else {
      // Handle case when data is null/empty (when all files are deleted)
      if (type === "damage_receipt") {
        newDocumentData.damagePayments = {};
      }
      if (type === "third_party_receipt") {
        newDocumentData.thirdPartyPayments = {};
      }
      if (type === "recourse_receipt") {
        newDocumentData.recoursePayments = {};
      }
      if (type === "depreciation_receipt") {
        newDocumentData.depreciationPayments = {};
      }
      setDocumentData((prev) => ({
        ...prev,
        ...newDocumentData,
      }));
    }
  };

  const handleManualInputsSave = (values) => {
    console.log("Manual inputs saved:", values);
    setDocumentData((prevData) => ({
      ...prevData,
      ...values,
    }));
  };

  // Handler for pre_created_document processing
  const handlePreCreatedDocumentProcessed = async (requestId) => {
    // If requestId is empty or undefined, show error immediately and refresh
    if (!requestId) {
      showErrorModalWithRedirect(
        "Belge bulunamadı. Sistem de hata olmuş ya da bu belge bu sistemde üretilmemiş olabilir."
      );
      return;
    }
    console.log("Processing pre-created document with requestId:", requestId);

    try {
      const { getRequestDocumentData } = await import("./services/firestore");
      const requestData = await getRequestDocumentData(requestId);

      if (requestData) {
        console.log("Loaded request document:", requestData);
        console.log("Switching to template:", requestData.templateId);
        console.log("Document data loaded:", requestData.documentData);

        // Find the template to check required documents
        const template = templates.find((t) => t.id === requestData.templateId);
        if (template) {
          // Get required documents (inputDocuments minus optionalDocuments)
          const optionalDocs = template.optionalDocuments || [];
          const requiredDocs = (template.inputDocuments || []).filter(
            (doc) => !optionalDocs.includes(doc)
          );

          console.log("Required documents for template:", requiredDocs);
          console.log(
            "Available documents in preloaded data:",
            Object.keys(requestData.documentData)
          );

          // Check if all required documents exist in preloaded data
          const missingDocs = requiredDocs.filter(
            (doc) =>
              !requestData.documentData[doc] ||
              !Array.isArray(requestData.documentData[doc]) ||
              requestData.documentData[doc].length === 0
          );

          if (missingDocs.length > 0) {
            console.log("Missing required documents:", missingDocs);
            showErrorModalWithRedirect(
              "Maalesef bu dosya eski bir versiyonla üretilmiş, işleminizi gerçekleştiremiyoruz."
            );
            return;
          }
        }

        // Switch to the template from the request document without triggering full reset
        skipNextReset.current = true;
        setDocumentType(requestData.templateId);

        // Apply payment calculations to the loaded data
        const calculatedData = calculatePayments(requestData.documentData);
        setDocumentData((prev) => ({
          ...prev,
          ...calculatedData,
          documentType: requestData.templateId,
        }));
        setDocumentOrder(requestData.documentOrder || []);

        console.log("Pre-created document flow completed successfully");
      } else {
        console.log("No request document found for requestId:", requestId);

        showErrorModalWithRedirect(
          "Belge bulunamadı. Sistem de hata olmuş ya da bu belge bu sistemde üretilmemiş olabilir."
        );
      }
    } catch (error) {
      console.error("Error processing pre-created document:", error);
    }
  };

  const handleDocumentOrderChange = (order) => {
    console.log("Document order changed:", order);
    setDocumentOrder(order);
  };

  const [showDocumentTypeOptions, setShowDocumentTypeOptions] = useState(false);

  // Add useEffect to handle outside clicks for dropdown
  useEffect(() => {
    if (showDocumentTypeOptions) {
      const handleOutsideClick = (e) => {
        const wrapper = document.querySelector(
          ".document-type-selector-wrapper"
        );
        if (wrapper && !wrapper.contains(e.target)) {
          setShowDocumentTypeOptions(false);
        }
      };

      document.addEventListener("click", handleOutsideClick);

      return () => {
        document.removeEventListener("click", handleOutsideClick);
      };
    }
  }, [showDocumentTypeOptions]);

  const handleDocumentTypeClick = (e) => {
    // If dropdown disabled, do nothing
    if (isSingleTemplate) return;

    // Toggle dropdown when clicking on wrapper or select div, but not when clicking on options list
    if (
      e.target.classList.contains("document-type-selector-wrapper") ||
      e.target.classList.contains("document-type-selector-select") ||
      e.target.parentElement.classList.contains("document-type-selector-select")
    ) {
      setShowDocumentTypeOptions(!showDocumentTypeOptions);
    }
  };

  // Ref to access dropdown options container
  const dropdownOptionsRef = useRef(null);

  // Save dropdown scroll position on scroll
  const handleDropdownScroll = (e) => {
    const scrollTop = e.target.scrollTop;
    localStorage.setItem("dropdownScrollPos", scrollTop);
  };

  // Restore scroll position whenever options become visible
  useEffect(() => {
    if (showDocumentTypeOptions) {
      const savedPos = parseInt(
        localStorage.getItem("dropdownScrollPos") || "0",
        10
      );
      if (dropdownOptionsRef.current) {
        dropdownOptionsRef.current.scrollTop = savedPos;
      }
    }
  }, [showDocumentTypeOptions]);

  const handleOptionSelect = (templateId) => {
    setDocumentType(templateId); // This will trigger the useEffect below
    setShowDocumentTypeOptions(false);
    // Persist selected template
    localStorage.setItem("selectedTemplateId", templateId);
  };

  // Effect to reset data when documentType changes (after initial setup)
  useEffect(() => {
    if (isInitialDocumentTypeSet.current) {
      isInitialDocumentTypeSet.current = false;
    } else {
      // Only run if documentType has a value (i.e., a template is selected)
      if (documentType) {
        // If skip flag is set, clear it and do not reset
        if (skipNextReset.current) {
          console.log(
            "Document type changed programmatically, skipping reset."
          );
          skipNextReset.current = false;
          return;
        }
        console.log("Document type changed, resetting data for:", documentType);
        handleResetData();
      }
    }
  }, [documentType]);

  // Adaptive background color logic for single-column children
  useEffect(() => {
    const updateBackgrounds = () => {
      const container = document.querySelector(".single-column");
      if (!container) return;
      const children = Array.from(container.children);
      const opacities = [1, 0.85, 0.7, 0.85, 1, 0.85, 0.7, 0.85, 1];
      children.forEach((child, idx) => {
        child.style.backgroundColor = `rgba(250,245,236,${
          opacities[idx % opacities.length]
        })`;
      });
    };

    // Initial update
    updateBackgrounds();

    const container = document.querySelector(".single-column");
    if (!container) return;
    const observer = new window.MutationObserver(updateBackgrounds);
    observer.observe(container, { childList: true, subtree: false });

    // Also update on window resize (in case layout changes)
    window.addEventListener("resize", updateBackgrounds);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updateBackgrounds);
    };
  }, [selectedTemplate, allFileUploadsComplete, documentData]);

  const handleResetData = async () => {
    let currentDocumentType = documentType;
    console.log(
      filteredTemplates,
      currentDocumentType,
      "filteredTemplates",
      !filteredTemplates.some((t) => t.id === currentDocumentType)
    );
    if (!filteredTemplates.some((t) => t.id === currentDocumentType)) {
      currentDocumentType = "pre_created_document";
      setDocumentType(currentDocumentType);
    }

    console.log("Creating new request ID for reset...");
    // Create a new request ID for the new session
    await createNewRequest();

    // Reset file upload states in UploadContext
    if (resetUploadContextState) {
      resetUploadContextState();
    }

    // Reset App.js states
    setDocumentData((prev) => ({
      documentType: currentDocumentType, // Keep the current document type
      damagePayments: {},
      thirdPartyPayments: {},
      depreciationPayments: {},
    }));
    setDocumentOrder([]);
    setAdditionalNotes("");
    setConsolidationApplicationNumber("");

    // If there's a selected template, re-apply its initial required uploads
    if (selectedTemplate && setRequiredUploadsForTemplate) {
      setRequiredUploadsForTemplate(
        selectedTemplate.inputDocuments || [],
        selectedTemplate.optionalDocuments || []
      );
    }

    // Optionally, you might want to re-fetch templates or re-initialize documentType
    // if the first template should be selected by default after a reset.
    // For now, we keep the current documentType.
    setFileUploadResetKey((prevKey) => prevKey + 1);
  };

  const [errorModalVisible, setErrorModalVisible] = useState(false);
  const [errorModalMessage, setErrorModalMessage] = useState("");
  const errorModalTimerRef = useRef(null);

  // Helper to show error modal with auto redirect
  const showErrorModalWithRedirect = (message) => {
    // Clear any existing timer
    if (errorModalTimerRef.current) {
      clearTimeout(errorModalTimerRef.current);
    }

    setErrorModalMessage(
      `${message} 5 saniye içinde ana sayfaya yönlendirileceksiniz.`
    );
    setErrorModalVisible(true);

    // Auto redirect after 5 seconds
    errorModalTimerRef.current = setTimeout(() => {
      setErrorModalVisible(false);
      handleResetData();
      errorModalTimerRef.current = null;
    }, 5000);
  };

  // Handler for immediate redirect when user clicks "Tamam"
  const handleImmediateRedirect = () => {
    if (errorModalTimerRef.current) {
      clearTimeout(errorModalTimerRef.current);
      errorModalTimerRef.current = null;
    }
    setErrorModalVisible(false);
    handleResetData();
  };

  // Helper modal component for error display (same style as PlaceholderModal in ResultSummary)
  const ErrorModal = () => {
    if (!errorModalVisible) return null;

    return (
      <div
        className="modal-overlay"
        onClick={() => setErrorModalVisible(false)}
      >
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-body">
            <p>{errorModalMessage}</p>
          </div>
          <div className="modal-buttons">
            <button
              className="modal-btn primary"
              onClick={handleImmediateRedirect}
            >
              Tamam
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Close dropdown automatically if only one template is available
  useEffect(() => {
    if (isSingleTemplate && showDocumentTypeOptions) {
      setShowDocumentTypeOptions(false);
    }
  }, [isSingleTemplate, showDocumentTypeOptions]);

  return (
    <>
      <div className="header-container">
        <div className="user-profile">
          <div className="user-info">
            {user ? (
              <>
                <p className="user-name">{user.name || user.email || "User"}</p>
                <div className="user-links">
                  <span
                    className="header-link"
                    onClick={async () => {
                      // Reset application state just like when document type changes
                      await handleResetData();

                      if (typeof onReturnToCategories === "function") {
                        onReturnToCategories();
                      } else {
                        navigate("/");
                      }
                    }}
                  >
                    Geri dön
                  </span>
                  <span className="header-separator">·</span>
                  <span
                    className="header-link"
                    onClick={() =>
                      logout({
                        logoutParams: { returnTo: window.location.origin },
                      })
                    }
                  >
                    Çıkış Yap
                  </span>
                </div>
              </>
            ) : (
              <p className="user-name">...</p>
            )}
          </div>
          {user ? (
            user.picture ? (
              <img
                src={user.picture}
                alt="Profile"
                className="user-icon"
                style={{ width: "40px", height: "40px", borderRadius: "50%" }}
              />
            ) : (
              <div className="user-icon">
                {(
                  user.given_name?.[0] ||
                  user.name?.[0] ||
                  user.email?.[0] ||
                  "U"
                ).toUpperCase()}
              </div>
            )
          ) : (
            <div className="user-icon">...</div>
          )}
        </div>
        <div
          className="document-type-selector-wrapper"
          style={{
            position: "relative",
            display: "inline-block",
            cursor: "pointer",
            visibility: isSingleTemplate ? "hidden" : "visible",
          }}
          onClick={handleDocumentTypeClick}
        >
          <div
            className="document-type-selector-select"
            style={{
              /* Keep large right padding from CSS (60px) to avoid overlapping with the dropdown icon */
              paddingLeft: 12,
              height: "28px",
              lineHeight: "28px",
            }}
          >
            {templatesLoading
              ? "Yükleniyor..."
              : filteredTemplates.length > 0
              ? filteredTemplates.find(
                  (template) => template.id === documentType
                )?.name || "Şablon Seçin"
              : "Şablon bulunamadı"}
          </div>
          <svg
            className="document-type-selector-icon"
            width="36"
            height="36"
            viewBox="0 0 24 24"
            fill="#5772A3"
            xmlns="http://www.w3.org/2000/svg"
            style={{
              position: "absolute",
              right: 12,
              top: "50%",
              transform: "translateY(-50%)",
              pointerEvents: "none",
            }}
          >
            <polygon points="12,16 6,9 18,9" />
          </svg>

          {showDocumentTypeOptions && !isSingleTemplate && (
            <div
              className="document-type-options"
              ref={dropdownOptionsRef}
              onScroll={handleDropdownScroll}
            >
              {templatesLoading ? (
                <div className="document-type-option">Yükleniyor...</div>
              ) : filteredTemplates.length > 0 ? (
                <>
                  {/* Flat list of templates */}
                  {filteredTemplates.map((template) => (
                    <div
                      key={template.id}
                      className="document-type-option"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOptionSelect(template.id);
                      }}
                    >
                      {template.name}
                    </div>
                  ))}
                </>
              ) : (
                <div className="document-type-option">Şablon bulunamadı</div>
              )}
            </div>
          )}
        </div>
      </div>
      <div className="upload-container">
        <div className="single-column">
          {templatesError && (
            <div style={{ color: "red", marginTop: "5px" }}>
              Hata: {templatesError}
            </div>
          )}
          {selectedTemplate?.inputDocuments?.map((docId, idx) => {
            return (
              <FileUpload
                key={`${docId}-${fileUploadResetKey}`}
                title={selectedTemplate?.dictionary?.[docId] || docId}
                id={docId}
                className={
                  (docId.includes("damage_receipt") ||
                  docId.includes("third_party_receipt") ||
                  docId.includes("recourse_receipt") ||
                  docId.includes("depreciation_receipt") ||
                  docId.includes("additional_document")
                    ? "multi-file "
                    : "") + (idx === 0 ? "first-upload-box" : "")
                }
                onDataUpdate={(data) => handleDocumentDataUpdate(docId, data)}
                selectedTemplateId={documentType}
                dictionary={selectedTemplate?.dictionary}
                onPreCreatedDocumentProcessed={
                  docId === "pre_created_document"
                    ? handlePreCreatedDocumentProcessed
                    : undefined
                }
                preloadedDocumentData={documentData}
              />
            );
          })}
          {allFileUploadsComplete && (
            <DocumentOrderSelector
              onOrderChange={handleDocumentOrderChange}
              selectedTemplate={selectedTemplate}
              documentData={documentData}
              initialDocumentOrder={documentOrder}
            />
          )}
          {allFileUploadsComplete && (
            <AdditionalNotes
              manualInputs={selectedTemplate?.manualInputs}
              onSave={handleManualInputsSave}
              initialValues={documentData}
              dictionary={selectedTemplate?.dictionary}
            />
          )}
        </div>
        <div className="result-single-column">
          <ResultSummary
            documentData={documentData}
            documentOrder={documentOrder}
            onDocumentOrderChange={handleDocumentOrderChange}
            onResetData={handleResetData}
            selectedTemplate={selectedTemplate}
          />
        </div>
      </div>
      {/* Global error modal for pre_created_document issues */}
      <ErrorModal />
    </>
  );
}

/**
 * Wrapper component to manage whether the user is viewing the category selection
 * screen or the main application flow. This replaces the previous route-based
 * navigation to '/app' with an in-page state switch, preserving the ability to
 * return back to the category screen without a route change. Refreshing the
 * page will reset the state so the category selection is shown again.
 */
function MainPage() {
  // Holds the ID of the selected category. Null means no category selected yet.
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);

  return selectedCategoryId ? (
    <AppContent
      selectedCategoryId={selectedCategoryId}
      onReturnToCategories={() => setSelectedCategoryId(null)}
    />
  ) : (
    <CategorySelection onSelect={(cat) => setSelectedCategoryId(cat.id)} />
  );
}

function App() {
  const {
    isAuthenticated,
    isFirebaseAuthenticated,
    isLoading,
    error: firebaseError,
  } = useAuth0Firebase();

  if (isLoading) {
    console.log("[App] Overall isLoading is true, showing loading screen...");
    return (
      <div className="loading-container" style={{ backgroundColor: "#faf5ec" }}>
        <Lottie
          animationData={waitingAnimation}
          style={{ width: 400, height: 600 }}
        />
      </div>
    );
  }

  // If not authenticated, render LoginPage
  if (!isAuthenticated || !isFirebaseAuthenticated) {
    console.log("[App] Not fully authenticated, showing LoginPage.", {
      isAuthenticated,
      isFirebaseAuthenticated,
      firebaseError,
    });
    // return <LoginPage />; // Render LoginPage here - this line will be handled by the router for the '/' path
  }

  console.log("[App] Rendering authenticated app");
  return (
    <Router>
      <RequestProvider>
        <DocumentTemplatesProvider>
          <UploadProvider>
            <Routes>
              {/* Route for Access Denied Page */}
              <Route path="/access-denied" element={<AccessDeniedPage />} />

              {/* Main page (category selection + flow) */}
              <Route
                path="/"
                element={
                  isAuthenticated && isFirebaseAuthenticated ? (
                    <MainPage />
                  ) : (
                    <LoginPage />
                  )
                }
              />
              {/* Redirect legacy /app path (used before refactor) back to the root */}
              <Route path="/app/*" element={<Navigate to="/" replace />} />
              {/* Add other routes here if needed */}
            </Routes>
          </UploadProvider>
        </DocumentTemplatesProvider>
      </RequestProvider>
    </Router>
  );
}

export default App;
